/**
 * @file 手書き (学びメモに添付する線の集まり) のデータと描画の純関数である。
 *
 * @what: 1本の線は色・太さ・道具 (ペン / 消しゴム) と、座標と筆圧の列で持つ。文書は論理座標
 *   (幅1200 × 高さ900) で持ち、画面の大きさに関わらず同じ形になる。線の輪郭は perfect-freehand で
 *   出し、Canvas に塗る。取り消し / やり直しは線の単位で行う。
 * @why: 座標を残せば、画像にしたあとでも開いて書き足せる。画面ごとに座標を変えると端末をまたいで
 *   崩れるので、論理座標に固定して表示するときだけ拡大縮小する。
 * @failure: 例外を投げない。読めない JSON は null にする。
 */

import { getStroke } from "perfect-freehand";

/** 文書の論理座標の大きさ (4:3)。 */
export const HANDWRITING_WIDTH = 1200;
export const HANDWRITING_HEIGHT = 900;

export type HandwritingTool = "pen" | "eraser";

/** 線の1点である (x, y, 筆圧は0〜1)。 */
export type HandwritingPoint = readonly [number, number, number];

export interface HandwritingStroke {
  tool: HandwritingTool;
  /** ペンの色 (消しゴムでは使わない)。 */
  color: string;
  /** 論理座標での太さである。 */
  size: number;
  /** ペンで書いたか (筆圧が本物か)。指やマウスは筆圧を速度から作る。 */
  pen: boolean;
  points: HandwritingPoint[];
}

export interface HandwritingDoc {
  v: 1;
  width: number;
  height: number;
  strokes: HandwritingStroke[];
}

export const PEN_COLORS: readonly { id: string; label: string; color: string }[] = [
  { id: "black", label: "黒", color: "#111827" },
  { id: "red", label: "赤", color: "#c0392b" },
];

export const PEN_SIZES: readonly { id: string; label: string; size: number }[] = [
  { id: "thin", label: "細", size: 4 },
  { id: "medium", label: "中", size: 8 },
  { id: "thick", label: "太", size: 14 },
];

/** 消しゴムの太さ (論理座標)。 */
export const ERASER_SIZE = 40;

/** 紙の色 (消しゴムはこの色で塗る)。 */
export const PAPER_COLOR = "#ffffff";

export function emptyHandwriting(): HandwritingDoc {
  return { v: 1, width: HANDWRITING_WIDTH, height: HANDWRITING_HEIGHT, strokes: [] };
}

/** 線の輪郭 (多角形) を出す。 */
export function strokeOutline(stroke: HandwritingStroke): number[][] {
  return getStroke(
    stroke.points.map(([x, y, p]) => [x, y, p]),
    {
      size: stroke.tool === "eraser" ? ERASER_SIZE : stroke.size,
      thinning: stroke.tool === "eraser" ? 0 : 0.55,
      smoothing: 0.5,
      streamline: 0.45,
      simulatePressure: !stroke.pen,
      last: true,
    },
  );
}

/** 多角形を Canvas のパスにする (中点を通る2次曲線でつなぐ)。 */
export function outlineToPath(outline: readonly number[][]): Path2D {
  const path = new Path2D();
  if (outline.length === 0) return path;
  const [x0, y0] = outline[0]!;
  path.moveTo(x0!, y0!);
  for (let i = 1; i < outline.length; i++) {
    const [x, y] = outline[i]!;
    const [nx, ny] = outline[(i + 1) % outline.length]!;
    path.quadraticCurveTo(x!, y!, (x! + nx!) / 2, (y! + ny!) / 2);
  }
  path.closePath();
  return path;
}

/**
 * 線を1本塗る。scale は論理座標 → 描画先の倍率である。
 * transparent が真のとき (ページの上の書き込み) は、消しゴムを紙の色で塗らず本当に消す。
 */
export function paintStroke(
  ctx: CanvasRenderingContext2D,
  stroke: HandwritingStroke,
  scale: number,
  transparent = false,
): void {
  if (stroke.points.length === 0 || typeof Path2D === "undefined") return;
  ctx.save();
  ctx.scale(scale, scale);
  if (stroke.tool === "eraser" && transparent) {
    ctx.globalCompositeOperation = "destination-out";
    ctx.fillStyle = "#000";
  } else {
    ctx.fillStyle = stroke.tool === "eraser" ? PAPER_COLOR : stroke.color;
  }
  ctx.fill(outlineToPath(strokeOutline(stroke)));
  ctx.restore();
}

/**
 * 文書を丸ごと表示する。transparent が偽なら紙の色で塗ってから線を重ね (メモの手書き)、
 * 真なら下地を塗らず線だけを重ねる (ページの上の書き込み)。
 */
export function paintHandwriting(
  ctx: CanvasRenderingContext2D,
  doc: HandwritingDoc,
  scale: number,
  transparent = false,
): void {
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, doc.width * scale, doc.height * scale);
  if (!transparent) {
    ctx.fillStyle = PAPER_COLOR;
    ctx.fillRect(0, 0, doc.width * scale, doc.height * scale);
  }
  ctx.restore();
  for (const s of doc.strokes) paintStroke(ctx, s, scale, transparent);
}

/** 保存する JSON の形にする。 */
export function serializeHandwriting(doc: HandwritingDoc): string {
  return JSON.stringify(doc);
}

/** 保存した JSON を読む。形が違えば null を返す。 */
export function parseHandwriting(raw: string): HandwritingDoc | null {
  try {
    const v = JSON.parse(raw) as unknown;
    if (!v || typeof v !== "object") return null;
    const d = v as Partial<HandwritingDoc>;
    if (d.v !== 1 || !Array.isArray(d.strokes)) return null;
    const strokes: HandwritingStroke[] = [];
    for (const s of d.strokes as unknown[]) {
      if (!s || typeof s !== "object") return null;
      const st = s as Partial<HandwritingStroke>;
      if ((st.tool !== "pen" && st.tool !== "eraser") || !Array.isArray(st.points)) return null;
      const points: HandwritingPoint[] = [];
      for (const p of st.points as unknown[]) {
        if (!Array.isArray(p) || p.length < 2) return null;
        const [x, y, pr] = p as number[];
        if (typeof x !== "number" || typeof y !== "number") return null;
        points.push([x, y, typeof pr === "number" ? pr : 0.5]);
      }
      strokes.push({
        tool: st.tool,
        color: typeof st.color === "string" ? st.color : PEN_COLORS[0]!.color,
        size: typeof st.size === "number" ? st.size : PEN_SIZES[1]!.size,
        pen: st.pen === true,
        points,
      });
    }
    return {
      v: 1,
      width: typeof d.width === "number" ? d.width : HANDWRITING_WIDTH,
      height: typeof d.height === "number" ? d.height : HANDWRITING_HEIGHT,
      strokes,
    };
  } catch {
    return null;
  }
}

/** 手書きの画像の id か (本文の参照から編集の入口を出すための目印)。 */
export const HANDWRITING_ID_PREFIX = "hw-";

export function isHandwritingImageId(id: string): boolean {
  return id.startsWith(HANDWRITING_ID_PREFIX);
}

/** 画像のパスから、線の JSON のパスを出す。 */
export function handwritingStrokesPath(imagePath: string): string {
  return imagePath.replace(/\.png$/, "") + ".strokes.json";
}
